import { useEffect, useRef } from 'react';
import './LiveSpectrogram.css';

export interface LiveSpectrogramProps {
  /** Open analyser the panel is reading off. When null, the canvas pauses. */
  analyser: AnalyserNode | null;
  /** Optional AudioContext — when provided we call resume() if it's
   *  suspended on every paint. Without this, a context that started
   *  suspended (no user gesture in scope) stays suspended and the canvas
   *  paints silence forever. */
  context?: AudioContext | null;
  /** When true, draw a "current 1-second window" highlight on the right
   *  edge of the canvas — used in the predict panel to show the kid which
   *  slice of audio the model is actually classifying right now. */
  highlightWindow?: boolean;
  /** Optional explicit canvas width. Defaults to the parent's width. */
  width?: number;
  height?: number;
  /** Pixel-columns per second of audio; controls scroll speed. */
  pixelsPerSecond?: number;
  /** Brightness boost so quiet bins still show movement. */
  contrast?: number;
}

const DEFAULT_HEIGHT = 80;
const DEFAULT_WIDTH = 320;
const DEFAULT_PIXELS_PER_SECOND = 60;
const DEFAULT_CONTRAST = 1.1;

/**
 * Continuously paints the live audio spectrum off an AnalyserNode as a
 * scrolling waveform. Each animation tick we read `getFloatFrequencyData`,
 * crop to the lower bins, and shift the canvas left by one column so the
 * newest frame appears on the right edge — just like TM's recording
 * indicator. Pure visual: no state, no React updates per frame, so it
 * never causes re-renders of the panel that owns it.
 */
export function LiveSpectrogram({
  analyser,
  context,
  highlightWindow = false,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  pixelsPerSecond = DEFAULT_PIXELS_PER_SECOND,
  contrast = DEFAULT_CONTRAST,
}: LiveSpectrogramProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Best-effort resume: a suspended AudioContext makes the analyser
    // produce -Infinity for every bin and the spectrogram looks dead.
    // The mic panel passes the context through; we kick off a resume on
    // mount and let the per-paint loop keep retrying if it bounces back.
    if (context && context.state === 'suspended') {
      void context.resume().catch(() => {
        // best-effort
      });
    }

    // Reset the canvas on each fresh analyser hookup so we never paint
    // stale frames from a previous mic open.
    ctx.fillStyle = '#0b1020';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const bins = Math.min(analyser.frequencyBinCount, 256);
    const frame = new Float32Array(analyser.frequencyBinCount);

    let stopped = false;
    let lastDraw = performance.now();
    const msPerColumn = 1000 / pixelsPerSecond;

    const tick = (): void => {
      if (stopped) return;
      const now = performance.now();
      const elapsed = now - lastDraw;
      if (elapsed < msPerColumn) {
        requestAnimationFrame(tick);
        return;
      }
      const columns = Math.min(8, Math.floor(elapsed / msPerColumn));
      lastDraw = now;
      // Scroll the existing image left by `columns` pixels.
      const imageData = ctx.getImageData(columns, 0, canvas.width - columns, canvas.height);
      ctx.putImageData(imageData, 0, 0);

      analyser.getFloatFrequencyData(frame);
      // Paint `columns` fresh columns on the right edge using the current
      // frame. We re-use the same frame for the small batch since the
      // mic doesn't change that fast at this scroll speed.
      for (let c = 0; c < columns; c++) {
        const x = canvas.width - columns + c;
        for (let y = 0; y < canvas.height; y++) {
          // Map pixel y (top = high freq) to a bin. We only show the
          // lower frequency range — speech, claps, whistles all live
          // there.
          const binIndex = Math.floor(((canvas.height - 1 - y) / canvas.height) * bins);
          const v = frame[binIndex] ?? -100;
          // dBFS [-100, 0] → [0, 1]
          const intensity = Math.max(0, Math.min(1, (v + 100) / 100));
          const boosted = Math.min(1, intensity * contrast);
          const { r, g, b } = mapColor(boosted);
          ctx.fillStyle = `rgb(${r},${g},${b})`;
          ctx.fillRect(x, y, 1, 1);
        }
      }

      if (highlightWindow) {
        // Draw the "current 1 second window" frame around the rightmost
        // `pixelsPerSecond` columns. The kid sees this box drift across
        // the recording strip so they understand what slice of audio
        // the model is actually classifying.
        ctx.strokeStyle = 'rgba(99, 102, 241, 0.95)';
        ctx.lineWidth = 2;
        const boxX = canvas.width - pixelsPerSecond;
        ctx.strokeRect(boxX + 1, 1, pixelsPerSecond - 2, canvas.height - 2);
      }

      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    return () => {
      stopped = true;
    };
  }, [analyser, context, highlightWindow, pixelsPerSecond, contrast]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="bl-live-spec"
      role="img"
      aria-label="Live audio spectrum"
    />
  );
}

function mapColor(intensity: number): { r: number; g: number; b: number } {
  // Dark navy → blue → indigo → pink, matching the studio palette and
  // close to the "Inferno" lookup that TM uses.
  const stops: Array<[number, [number, number, number]]> = [
    [0, [11, 16, 32]],
    [0.35, [37, 67, 184]],
    [0.65, [120, 81, 200]],
    [0.85, [220, 80, 145]],
    [1, [253, 191, 144]],
  ];
  for (let i = 1; i < stops.length; i++) {
    const [t1, c1] = stops[i]!;
    if (intensity <= t1) {
      const [t0, c0] = stops[i - 1]!;
      const f = (intensity - t0) / Math.max(1e-6, t1 - t0);
      return {
        r: Math.round(c0[0] * (1 - f) + c1[0] * f),
        g: Math.round(c0[1] * (1 - f) + c1[1] * f),
        b: Math.round(c0[2] * (1 - f) + c1[2] * f),
      };
    }
  }
  const last = stops[stops.length - 1]![1];
  return { r: last[0], g: last[1], b: last[2] };
}
